import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Bug, MessageSquarePlus, LifeBuoy, CheckCircle2, RotateCcw, Inbox } from "lucide-react";

const catMeta = {
  bug: { label: "Bug", icon: Bug, cls: "bg-red-500/15 text-red-300 border-red-500/25" },
  feedback: { label: "Feedback", icon: MessageSquarePlus, cls: "bg-[#ccff0c]/15 text-[#ccff0c] border-[#ccff0c]/25" },
  other: { label: "Other", icon: LifeBuoy, cls: "bg-secondary text-muted-foreground border-border" },
};

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("open");

  const load = () => api.get("/admin/support/tickets").then(({ data }) => setTickets(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/support/tickets/${id}/status?status=${status}`);
      toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const shown = tickets.filter((t) => (filter === "all" ? true : t.status === filter));

  return (
    <div className="mt-4 space-y-3" data-testid="admin-support">
      <div className="flex gap-2">
        {["open", "resolved", "all"].map((f) => (
          <button key={f} data-testid={`support-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize border transition-colors ${filter === f ? "bg-[#ccff0c] text-black border-[#ccff0c]" : "bg-card text-muted-foreground border-border"}`}>
            {f} {f !== "all" ? `(${tickets.filter((t) => t.status === f).length})` : ""}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Card className="rounded-2xl bg-card border-border p-8 text-center">
          <Inbox className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">No {filter !== "all" ? filter : ""} tickets.</p>
        </Card>
      ) : (
        shown.map((t) => {
          const meta = catMeta[t.category] || catMeta.other;
          const Icon = meta.icon;
          return (
            <Card key={t.id} data-testid={`admin-ticket-${t.id}`} className="rounded-2xl bg-card border-border p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`border text-[10px] flex items-center gap-1 ${meta.cls}`}><Icon className="h-3 w-3" /> {meta.label}</Badge>
                    {t.status === "resolved" && <Badge className="border text-[10px] bg-[#ccff0c]/15 text-[#ccff0c] border-[#ccff0c]/25">Resolved</Badge>}
                  </div>
                  <p className="font-semibold text-sm truncate">{t.subject}</p>
                  <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{t.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-2">{t.user_name || "User"} · {t.user_email} · {new Date(t.created_at).toLocaleString()}</p>
                </div>
              </div>
              <div className="mt-3">
                {t.status === "open" ? (
                  <Button data-testid={`resolve-ticket-${t.id}`} onClick={() => setStatus(t.id, "resolved")} className="h-8 text-xs bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark resolved
                  </Button>
                ) : (
                  <Button data-testid={`reopen-ticket-${t.id}`} onClick={() => setStatus(t.id, "open")} className="h-8 text-xs bg-secondary border border-border text-muted-foreground hover:border-border">
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen
                  </Button>
                )}
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}
